import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

export type FilterDropdownOption<T extends string> = {
  value: T
  label: string
  /** Tailwind bg-* class for the small status dot shown next to the option, e.g. "bg-amber-400" */
  dot?: string
}

type Props<T extends string> = {
  label: string
  value: T
  onChange: (value: T) => void
  options: FilterDropdownOption<T>[]
  /** Label for the "no filter" row. Defaults to "Все". */
  allLabel?: string
}

export function FilterDropdown<T extends string>({ label, value, onChange, options, allLabel = "Все" }: Props<T>) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value)
  const active = !!selected

  useEffect(() => {
    if (!open) return

    const updatePos = () => {
      const r = btnRef.current?.getBoundingClientRect()
      if (r) setPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 168) })
    }
    updatePos()

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (btnRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }

    window.addEventListener("scroll", updatePos, true)
    window.addEventListener("resize", updatePos)
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("scroll", updatePos, true)
      window.removeEventListener("resize", updatePos)
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const select = (v: T) => {
    onChange(v)
    setOpen(false)
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-lg text-xs font-medium border transition-colors ${
          active
            ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
        }`}
      >
        {selected?.dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${selected.dot}`} />}
        <span className="whitespace-nowrap">
          {active ? <><span className="text-gray-400 font-normal">{label}:</span> {selected!.label}</> : label}
        </span>
        {active ? (
          <span
            role="button"
            aria-label={`Сбросить ${label}`}
            onClick={(e) => { e.stopPropagation(); onChange("" as T) }}
            className="ml-0.5 p-0.5 rounded hover:bg-blue-200/60 flex-shrink-0"
          >
            <XMarkIcon className="w-3 h-3" />
          </span>
        ) : (
          <ChevronDownIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, minWidth: pos.width }}
          className="z-[70] bg-white border border-gray-200 rounded-xl shadow-lg shadow-black/5 py-1.5 max-h-80 overflow-auto"
        >
          <button
            type="button"
            onClick={() => select("" as T)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors hover:bg-gray-50 ${
              value === "" ? "text-blue-700 font-semibold" : "text-gray-500"
            }`}
          >
            <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
              {value === "" && <CheckIcon className="w-3.5 h-3.5 text-blue-600" />}
            </span>
            {allLabel}
          </button>

          <div className="my-1 border-t border-gray-100" />

          {options.map((opt) => {
            const isActive = opt.value === value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => select(opt.value)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors hover:bg-gray-50 ${
                  isActive ? "text-blue-700 font-semibold" : "text-gray-700"
                }`}
              >
                <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
                  {isActive
                    ? <CheckIcon className="w-3.5 h-3.5 text-blue-600" />
                    : opt.dot && <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} />
                  }
                </span>
                {opt.label}
              </button>
            )
          })}
        </div>,
        document.body
      )}
    </>
  )
}